/**
 * Step 2 — 웹 크롤링 소스 설정
 *
 * URL, 크롤링 깊이, 페이지네이션, 동기화 일정 설정
 */

import { useState, useEffect } from 'react';
import { WebConfig } from '../shared/pipeline.types';

interface WebSourceStepProps {
  config: WebConfig;
  onChange: (config: WebConfig) => void;
}

export function WebSourceStep({ config, onChange }: WebSourceStepProps) {
  const set = <K extends keyof WebConfig>(key: K, value: WebConfig[K]) =>
    onChange({ ...config, [key]: value });

  // 페이지 입력용 로컬 상태 (빈 값 허용)
  const [startPageInput, setStartPageInput] = useState(String(config.startPage));
  const [endPageInput, setEndPageInput] = useState(String(config.endPage));

  // config 값이 외부에서 변경되면 로컬 상태 동기화
  useEffect(() => {
    setStartPageInput(String(config.startPage));
  }, [config.startPage]);
  useEffect(() => {
    setEndPageInput(String(config.endPage));
  }, [config.endPage]);

  return (
    <div className="pipe-tab-content">
      <div className="form-grid">
        {/* URL + 깊이 */}
        <div className="web-source-url-grid">
          <div className="form-item">
            <label className="form-label">
              크롤링 URL <span className="required">*</span>
            </label>
            <input
              type="url"
              className="pipe-settings-input"
              placeholder="https://example.com/docs"
              value={config.url}
              onChange={(e) => set('url', e.target.value)}
            />
          </div>
          <div className="form-item">
            <label className="form-label">크롤링 깊이</label>
            <select
              className="pipe-settings-select"
              value={config.depth}
              onChange={(e) => set('depth', parseInt(e.target.value))}
            >
              <option value="1">1 (시작 페이지만)</option>
              <option value="2">2 (링크 1단계)</option>
              <option value="3">3 (링크 2단계)</option>
              <option value="5">5 (깊이 크롤링)</option>
            </select>
          </div>
        </div>

        {/* 페이지네이션 */}
        <div className="form-item pipe-pagination-section">
          <label className="form-label">
            페이지네이션 <span className="optional">(선택)</span>
          </label>
          <div className="pipe-pagination-row">
            <div className="pipe-pagination-field">
              <label>타입</label>
              <select
                className="pipe-settings-select"
                value={config.pageType}
                onChange={(e) => set('pageType', e.target.value as WebConfig['pageType'])}
              >
                <option value="query">쿼리 (?page=1)</option>
                <option value="path">경로 (/page/1)</option>
              </select>
            </div>
            <div className="pipe-pagination-field">
              <label>{config.pageType === 'path' ? '경로 패턴' : '파라미터명'}</label>
              <input
                type="text"
                className="pipe-settings-input"
                placeholder={config.pageType === 'path' ? 'page' : 'page'}
                value={config.pageParam}
                onChange={(e) => set('pageParam', e.target.value)}
              />
            </div>
            <div className="pipe-pagination-field pipe-pagination-num">
              <label>시작</label>
              <input
                type="number"
                className="pipe-settings-input"
                value={startPageInput}
                min={1}
                onChange={(e) => setStartPageInput(e.target.value)}
                onBlur={() => {
                  const num = parseInt(startPageInput);
                  const val = isNaN(num) || num < 1 ? 1 : num;
                  set('startPage', val);
                  setStartPageInput(String(val));
                }}
              />
            </div>
            <div className="pipe-pagination-field pipe-pagination-num">
              <label>끝</label>
              <input
                type="number"
                className="pipe-settings-input"
                value={endPageInput}
                min={1}
                onChange={(e) => setEndPageInput(e.target.value)}
                onBlur={() => {
                  const num = parseInt(endPageInput);
                  const val = isNaN(num) || num < 1 ? 1 : num;
                  set('endPage', val);
                  setEndPageInput(String(val));
                }}
              />
            </div>
          </div>
          <div className="web-source-pagination-hint">
            {config.pageType === 'path'
              ? `예: /${config.pageParam || 'page'}/1 ~ /${config.pageParam || 'page'}/${config.endPage}`
              : `예: ?${config.pageParam || 'page'}=1 ~ ?${config.pageParam || 'page'}=${config.endPage}`}
          </div>
        </div>

        {/* 동기화 일정 */}
        <div className="form-item">
          <label className="form-label">동기화 일정</label>
          <select
            className="pipe-settings-select"
            value={config.schedule}
            onChange={(e) => set('schedule', e.target.value as WebConfig['schedule'])}
          >
            <option value="manual">수동</option>
            <option value="daily">매일</option>
            <option value="weekly">매주</option>
          </select>
        </div>
      </div>
    </div>
  );
}
